interface DropdownInstance {
	root: HTMLElement;
	trigger: HTMLButtonElement;
	labelEl: HTMLElement;
	panel: HTMLElement;
	options: HTMLButtonElement[];
	input: HTMLInputElement;
	placeholder: string;
}

const S = {
	root: '.js-dropdown',
	trigger: '.js-dropdown-trigger',
	panel: '.js-dropdown-content',
	option: '.js-dropdown-button',
	value: 'dropdown__value',
	label: 'dropdown__label',
	open: 'is-open',
};

export function initDropdowns(scope: Document | HTMLElement = document) {
	Array.from(scope.querySelectorAll<HTMLElement>(S.root)).forEach(setup);
}

function setup(root: HTMLElement) {
	const trigger = root.querySelector<HTMLButtonElement>(S.trigger);
	const panel = root.querySelector<HTMLElement>(S.panel);
	const options = Array.from(root.querySelectorAll<HTMLButtonElement>(S.option));
	if (!trigger || !panel || options.length === 0) {
		return;
	}

	const labelEl = ensureLabel(trigger);
	const name = root.dataset.name || 'dropdown';
	const required = root.dataset.required === 'true';
	const input = ensureHiddenInput(root, name, required);

	const inst: DropdownInstance = {
		root,
		trigger,
		labelEl,
		panel,
		options,
		input,
		placeholder: labelEl.textContent?.trim() || 'Bitte auswählen',
	};

	ensureAria(inst);

	trigger.addEventListener('click', (e) => {
		e.preventDefault();
		isOpen(inst) ? close(inst) : open(inst);
	});

	trigger.addEventListener('keydown', (e) => onTriggerKeyDown(e, inst));
	panel.addEventListener('keydown', (e) => onPanelKeyDown(e, inst));
	document.addEventListener('click', (e) => onDocumentClick(e, inst), { capture: true });
	document.addEventListener('focusin', (e) => onDocumentFocusIn(e, inst));
	window.addEventListener('resize', () => close(inst));

	options.forEach((btn) =>
		btn.addEventListener('click', (e) => {
			e.preventDefault();
			selectOption(inst, btn);
		})
	);

	const form = root.closest('form');
	if (form) {
		form.addEventListener('reset', () => reset(inst));
	}
}

function ensureLabel(trigger: HTMLButtonElement): HTMLElement {
	const existing = trigger.querySelector<HTMLElement>(`.${S.label}`);
	if (existing) {
		return existing;
	}
	const label = document.createElement('span');
	label.className = S.label;
	const icon = trigger.querySelector<HTMLElement>('.icn');
	const text = (trigger.textContent || '').trim();
	label.textContent = text;
	trigger.textContent = '';
	trigger.appendChild(label);
	if (icon) {
		trigger.appendChild(icon);
	}
	return label;
}

function ensureHiddenInput(root: HTMLElement, name: string, required: boolean): HTMLInputElement {
	let input = root.querySelector<HTMLInputElement>(`input[type="hidden"].${S.value}`);
	if (!input) {
		input = document.createElement('input');
		input.type = 'hidden';
		input.className = S.value;
		input.name = name;
		root.appendChild(input);
	} else if (!input.name) {
		input.name = name;
	}
	input.value = '';
	if (required) {
		input.classList.add('js-validate');
		input.setAttribute('data-validation', 'required');
	}
	return input;
}

function ensureAria(inst: DropdownInstance) {
	const { trigger, panel, options, root } = inst;
	if (!panel.id) {
		panel.id = uid(root, 'dropdown-panel');
	}
	if (!trigger.id) {
		trigger.id = uid(root, 'dropdown-trigger');
	}
	trigger.setAttribute('aria-haspopup', 'listbox');
	trigger.setAttribute('aria-expanded', 'false');
	trigger.setAttribute('aria-controls', panel.id);
	panel.setAttribute('role', 'listbox');
	panel.setAttribute('tabindex', '-1');
	options.forEach((btn) => {
		btn.setAttribute('role', 'option');
		if (!btn.id) {
			btn.id = uid(root, 'dropdown-option');
		}
		const value = btn.dataset.value ?? btn.textContent?.trim() ?? '';
		btn.dataset.value = value;
		btn.setAttribute('aria-selected', 'false');
	});
}

function uid(scope: Element, prefix: string): string {
	const base = `${prefix}-${Math.random().toString(36).slice(2, 8)}`;
	return scope.querySelector(`#${base}`) ? uid(scope, prefix) : base;
}

function isOpen(inst: DropdownInstance) {
	return inst.trigger.getAttribute('aria-expanded') === 'true';
}

function open(inst: DropdownInstance) {
	const { trigger, panel, options, input } = inst;
	trigger.setAttribute('aria-expanded', 'true');
	panel.classList.add(S.open);
	const current = options.find((o) => o.dataset.value === input.value) || options[0];
	queueMicrotask(() => current.focus());
}

function close(inst: DropdownInstance) {
	inst.trigger.setAttribute('aria-expanded', 'false');
	inst.panel.classList.remove(S.open);
}

function selectOption(inst: DropdownInstance, btn: HTMLButtonElement) {
	const { trigger, labelEl, options, input } = inst;
	options.forEach((o) => o.setAttribute('aria-selected', String(o === btn)));
	const value = btn.dataset.value ?? btn.textContent?.trim() ?? '';
	input.value = value;
	labelEl.textContent = btn.textContent ?? value;
	input.dispatchEvent(new Event('change', { bubbles: true }));
	close(inst);
	trigger.focus();
}

function onTriggerKeyDown(e: KeyboardEvent, inst: DropdownInstance) {
	const k = e.key;
	if (k === 'ArrowDown' || k === 'ArrowUp' || k === ' ' || k === 'Enter') {
		e.preventDefault();
		if (!isOpen(inst)) {
			open(inst);
		}
	} else if (k === 'Escape') {
		e.preventDefault();
		close(inst);
	}
}

function onPanelKeyDown(e: KeyboardEvent, inst: DropdownInstance) {
	const { options } = inst;
	const k = e.key;
	const idx = options.findIndex((o) => o === document.activeElement);
	const move = (i: number) => options[Math.max(0, Math.min(options.length - 1, i))].focus();
	switch (k) {
		case 'ArrowDown':
			e.preventDefault();
			move((idx >= 0 ? idx : -1) + 1);
			break;
		case 'ArrowUp':
			e.preventDefault();
			move((idx >= 0 ? idx : options.length) - 1);
			break;
		case 'Home':
			e.preventDefault();
			move(0);
			break;
		case 'End':
			e.preventDefault();
			move(options.length - 1);
			break;
		case 'Enter':
		case ' ':
			e.preventDefault();
			if (document.activeElement instanceof HTMLButtonElement) {
				selectOption(inst, document.activeElement);
			}
			break;
		case 'Escape':
			e.preventDefault();
			close(inst);
			inst.trigger.focus();
			break;
	}
}

function onDocumentClick(e: MouseEvent, inst: DropdownInstance) {
	const t = e.target as Node;
	if (!inst.root.contains(t)) {
		close(inst);
	}
}

function onDocumentFocusIn(e: FocusEvent, inst: DropdownInstance) {
	const t = e.target as Node;
	if (!inst.root.contains(t)) {
		close(inst);
	}
}

function reset(inst: DropdownInstance) {
	const { labelEl, input, options, placeholder } = inst;
	input.value = '';
	labelEl.textContent = placeholder;
	options.forEach((o) => o.setAttribute('aria-selected', 'false'));
	input.dispatchEvent(new Event('change', { bubbles: true }));
}

document.addEventListener('DOMContentLoaded', () => initDropdowns());
